import React from "react";
import {
  Alert,
  Button,
  Checkbox,
  Collapse,
  Form,
  InputNumber,
  Modal,
  notification,
  Select,
  Switch,
  Table,
  Tag,
} from "antd";
const FormItem = Form.Item;
const Option = Select.Option;
const Panel = Collapse.Panel;

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

class AdminSettings extends React.Component {
  constructor(props) {
    super(props);
    this.passiveRequestId = 0;
    this.passiveUnmounted = false;
    this.state = {
      loading: true,
      saving: false,
      testPdfLoading: false,
      settings: {
        adminNotificationEmails: [],
        autoRelanceEnabled: false,
        autoRelanceDaysBefore: 2,
      },
      /**
       * Bulk company status update state.
       */
      passiveModalVisible: false,
      passivePreviewLoading: false,
      passiveSaving: false,
      passiveConfirmed: false,

      /**
       * Paginated company preview.
       */
      passiveCompanies: [],
      passivePage: 1,
      passivePageSize: 6,
      passiveTotal: 0,
      passivePreviewToken: null,
      passiveExcludedCompanyIds: [],

      /**
       * Error handling.
       */
      passiveError: "",
    };
  }

  componentDidMount() {
    fetch("/api/settings/admin-notifications", {
      credentials: "include",
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "x-access-token": localStorage.getItem("x-access-token"),
      },
    })
      .then((res) => res.json())
      .then((data) => {
        this.setState({
          loading: false,
          settings: data.settings || this.state.settings,
        });
      });
  }

  /**
   * Invalidates pending preview requests when the component is unmounted.
   *
   * @returns {void}
   */
  componentWillUnmount() {
    this.passiveUnmounted = true;
    this.passiveRequestId += 1;
  }

  save = () => {
    this.props.form.validateFields((err, values) => {
      if (err) return;

      this.setState({ saving: true });
      fetch("/api/settings/admin-notifications", {
        credentials: "include",
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": localStorage.getItem("x-access-token"),
        },
        body: JSON.stringify({
          adminNotificationEmails: values.adminNotificationEmails || [],
          autoRelanceEnabled: values.autoRelanceEnabled,
          autoRelanceDaysBefore: values.autoRelanceDaysBefore,
        }),
      })
        .then((res) => res.json())
        .then((data) => {
          this.setState({
            saving: false,
            settings: data.settings || this.state.settings,
          });

          if (data.success) {
            notification.success({
              message: "Paramètres enregistrés",
              description: "La configuration admin a été mise à jour.",
            });
          } else {
            notification.error({
              message: "Erreur",
              description: data.error || "Impossible d'enregistrer les paramètres.",
            });
          }
        });
    });
  };


  generateTestInvoice = () => {
    this.setState({ testPdfLoading: true });
    fetch("/api/settings/invoice-test-pdf", {
      credentials: "include",
      method: "POST",
      headers: {
        "x-access-token": localStorage.getItem("x-access-token"),
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Le service de génération PDF est indisponible.");
        }
        return response.blob();
      })
      .then((pdf) => {
        const url = window.URL.createObjectURL(pdf);
        const link = document.createElement("a");
        link.href = url;
        link.download = "LeHubBezannes-Facture-test.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        notification.success({
          message: "Facture de test générée",
          description: "Le PDF a été téléchargé. Aucune facture n’a été enregistrée.",
        });
      })
      .catch((error) => {
        notification.error({
          message: "Génération impossible",
          description: error.message,
        });
      })
      .then(() => this.setState({ testPdfLoading: false }));
  };
  
  /**
   * Requests an authenticated JSON API and surfaces HTTP/network failures.
   *
   * @param {string} url - API endpoint.
   * @param {Object} [options={}] - Additional fetch options.
   * @returns {Promise<Object>} Parsed response payload.
   */
  requestBulkJson = async (url, options = {}) => {
    let response;

    try {
      response = await fetch(url, {
        credentials: "include",
        ...options,
        headers: {
          "Content-Type": "application/json",
          "x-access-token": localStorage.getItem("x-access-token"),
          ...(options.headers || {}),
        },
      });
    } catch (cause) {
      const error = new Error(
        "Connexion au serveur impossible. Vérifiez votre réseau."
      );

      error.code = "NETWORK_ERROR";

      throw error;
    }

    let data;

    try {
      data = await response.json();
    } catch (cause) {
      const error = new Error(
        "Le serveur a renvoyé une réponse invalide."
      );

      error.code = "BAD_RESPONSE";

      throw error;
    }

    if (!response.ok || !data || data.success !== true) {
      const error = new Error(
        (data && (data.message || data.error)) ||
          "Une erreur est survenue pendant le traitement."
      );

      error.code = data && data.code;
      error.status = response.status;

      throw error;
    }

    return data;
  };

  /**
   * Opens the confirmation modal and loads its first page.
   *
   * @returns {void}
   */
  openPassiveModal = () => {
    if (!this.props.user || !this.props.user.admin) {
      return;
    }

    this.setState(
      {
        passiveModalVisible: true,
        passiveCompanies: [],
        passiveTotal: 0,
        passivePreviewToken: null,
        passiveExcludedCompanyIds: [],
        passiveConfirmed: false,
        passiveError: "",
      },
      () => this.loadActiveCompanies(1)
    );
  };
 
  /**
   * Fetches one page of active companies and ignores stale responses.
   *
   * @param {number} [page=1] - One-based page index.
   * @returns {Promise<void>} Resolves after loading the preview.
   */
  loadActiveCompanies = async (page = 1) => {
    const requestId = ++this.passiveRequestId;

    this.setState({
      passivePreviewLoading: true,
      passiveError: "",
      passiveConfirmed: false,
    });

    try {
      const data = await this.requestBulkJson(
        "/api/company/active-to-passive?page=" +
          page +
          "&pageSize=" +
          this.state.passivePageSize,
        {
          method: "GET",
        }
      );

      if (
        this.passiveUnmounted ||
        requestId !== this.passiveRequestId
      ) {
        return;
      }

      this.setState({
        passiveCompanies: data.companies,
        passiveTotal: data.total,
        passivePage: data.page,
        passivePageSize: data.pageSize,
        passivePreviewToken: data.previewToken,
        passiveExcludedCompanyIds:
          data.previewToken === this.state.passivePreviewToken
            ? this.state.passiveExcludedCompanyIds
            : [],
        passivePreviewLoading: false,
      });
    } catch (error) {
      if (
        this.passiveUnmounted ||
        requestId !== this.passiveRequestId
      ) {
        return;
      }

      this.setState({
        passiveCompanies: [],
        passivePreviewToken: null,
        passivePreviewLoading: false,
        passiveError: error.message,
      });
    }
  }; 

  /**
   * Closes the modal unless a status update is already running.
   *
   * @returns {void}
   */
  closePassiveModal = () => {
    if (this.state.passiveSaving) {
      return;
    }

    this.passiveRequestId += 1;

    this.setState({
      passiveModalVisible: false,
      passiveConfirmed: false,
      passiveError: "",
    });
  };

  /**
   * Confirms and executes the validated bulk status change.
   *
   * @returns {Promise<void>} Resolves after the operation completes.
   */
  setAllCompaniesPassive = async () => {
    const {
      passiveConfirmed,
      passiveSaving,
      passivePreviewLoading,
      passivePreviewToken,
      passiveTotal,
      passiveExcludedCompanyIds,
    } = this.state;
    const selectedCount = passiveTotal - passiveExcludedCompanyIds.length;

    /**
     * Prevents unauthorized, incomplete or duplicate submissions.
     */
    if (
      !this.props.user ||
      !this.props.user.admin ||
      !passiveConfirmed ||
      passiveSaving ||
      passivePreviewLoading ||
      !passivePreviewToken ||
      selectedCount === 0
    ) {
      return;
    }

    this.setState({
      passiveSaving: true,
      passiveError: "",
    });

    try {
      const data = await this.requestBulkJson(
        "/api/company/active-to-passive",
        {
          method: "POST",
          body: JSON.stringify({
            expectedCount: passiveTotal,
            previewToken: passivePreviewToken,
            excludedCompanyIds: passiveExcludedCompanyIds,
          }),
        }
      );

      if (this.passiveUnmounted) {
        return;
      }

      this.passiveRequestId += 1;

      this.setState({
        passiveModalVisible: false,
        passiveSaving: false,
        passiveConfirmed: false,
      });

      /**
       * Displays the actual number of modified companies.
       */
      notification[data.warning ? "warning" : "success"]({
        message: data.warning
          ? "Opération terminée avec avertissement"
          : "Statuts mis à jour",
        description:
          data.modifiedCount +
          " entreprise(s) passée(s) en passif." +
          (data.warning ? " " + data.warning : ""),
        duration: 8,
      });
    } catch (error) {
      if (this.passiveUnmounted) {
        return;
      }

      this.setState({
        passiveSaving: false,
        passiveConfirmed: false,
        passiveError: error.message,
      });

      /**
       * Refreshes the preview if the active companies changed.
       */
      if (error.code === "PREVIEW_OUTDATED") {
        notification.warning({
          message: "La liste des entreprises a changé",
          description:
            "Le tableau est actualisé. Vérifiez-le avant de confirmer à nouveau.",
        });

        await this.loadActiveCompanies(1);
      } else if (
        error.code === "NETWORK_ERROR" ||
        error.code === "BAD_RESPONSE"
      ) {
        /**
         * The server may have applied the update before the
         * connection or response failed.
         */
        notification.warning({
          message: "Résultat de l'opération à vérifier",
          description:
            "La réponse du serveur est indisponible. " +
            "Consultez la liste actualisée avant de relancer l'action.",
          duration: 10,
        });

        await this.loadActiveCompanies(1);
      } else {
        notification.error({
          message: "Modification impossible",
          description: error.message,
          duration: 8,
        });
      }
    }
  };

  render() {
    const { getFieldDecorator } = this.props.form;
    const { settings } = this.state;
    const autoRelanceEnabled = this.props.form.getFieldValue(
      "autoRelanceEnabled"
    );
    const remindersEnabled =
      typeof autoRelanceEnabled === "boolean"
        ? autoRelanceEnabled
        : settings.autoRelanceEnabled;

    const selectedCompanyCount =
      this.state.passiveTotal -
      this.state.passiveExcludedCompanyIds.length;
    const selectedRowKeys = this.state.passiveCompanies
      .filter(
        (company) =>
          this.state.passiveExcludedCompanyIds.indexOf(String(company._id)) ===
          -1
      )
      .map((company) => company._id);
    return (
      <div style={{ maxWidth: 760 }}>
        <Form>
          <Collapse
            bordered={false}
            className="admin-settings-collapse"
            defaultActiveKey={["notifications"]}
          >
            <Panel
              header={
                <span style={{ fontSize: 18, fontWeight: 600 }}>
                  1. Actualités, évènements et adhésions
                </span>
              }
              key="notifications"
            >
              <p style={{ margin: "0 0 6px", color: "#666", fontWeight: 600 }}>
                Étape 1 : ajoutez les adresses des personnes à prévenir lorsqu'une
                demande est envoyée sur le portail.
              </p>
              <FormItem
                extra="Ces personnes sont prévenues par email lorsqu'une demande d'actualité, d'évènement ou d'adhésion est déposée. Ajoutez une adresse, validez avec Entrée, puis recommencez pour chaque destinataire."
                style={{ marginBottom: 0 }}
              >
                <div style={{ marginBottom: 6 }}>Emails administrateurs</div>
                {getFieldDecorator("adminNotificationEmails", {
                  initialValue: settings.adminNotificationEmails || [],
                  rules: [
                    {
                      validator: (rule, value, callback) => {
                        const invalidEmail = (value || []).find(
                          (email) => !isValidEmail(email)
                        );

                        if (invalidEmail) {
                          callback("Adresse email invalide : " + invalidEmail);
                          return;
                        }

                        callback();
                      },
                    },
                  ],
                })(
                  <Select
                    mode="tags"
                    style={{ width: "100%" }}
                    placeholder="Ajouter une adresse email"
                    tokenSeparators={[",", ";", " "]}
                  >
                    {(settings.adminNotificationEmails || []).map((email) => (
                      <Option key={email} value={email}>
                        {email}
                      </Option>
                    ))}
                  </Select>
                )}
              </FormItem>
              <p style={{ margin: "24px 0 12px", color: "#666", fontWeight: 600 }}>
                Étape 2 : activez les relances et choisissez combien de jours avant
                l'évènement elles doivent être envoyées.
              </p>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "12px 24px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span>Relances automatiques :</span>
                  {getFieldDecorator("autoRelanceEnabled", {
                    valuePropName: "checked",
                    initialValue: settings.autoRelanceEnabled,
                  })(<Switch checkedChildren="Actif" unCheckedChildren="Inactif" />)}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    color: remindersEnabled ? undefined : "#999",
                  }}
                >
                  <span>Jours avant l'évènement :</span>
                  {getFieldDecorator("autoRelanceDaysBefore", {
                    initialValue: settings.autoRelanceDaysBefore || 2,
                  })(
                    <InputNumber
                      disabled={!remindersEnabled}
                      min={0}
                      max={30}
                    />
                  )}
                </div>
              </div>
              <p style={{ margin: "24px 0 12px", color: "#666", fontWeight: 600 }}>
                Étape 3 : enregistrez vos choix.
              </p>
              <Button type="primary" loading={this.state.saving} onClick={this.save}>
                Enregistrer
              </Button>
            </Panel>
            <Panel
              header={<span style={{ fontSize: 18, fontWeight: 600 }}>2. Facture</span>}
              key="invoice"
            >
              <p style={{ marginBottom: 16, color: "#666" }}>
                Générez une facture de démonstration pour vérifier le document.
                Elle sera téléchargée sur votre ordinateur, sans être enregistrée
                ni envoyée par email.
              </p>
              <Button
                icon="file-pdf"
                loading={this.state.testPdfLoading}
                onClick={this.generateTestInvoice}
              >
                Générer une facture de test
              </Button>
            </Panel>

            {this.props.user && this.props.user.admin ? (
              <Panel
                header={<span style={{ fontSize: 18, fontWeight: 600 }}>3. Entreprises</span>}
                key="companies"
              >
                <p style={{ marginBottom: 16, color: "#666" }}>
                  Ouvrez la liste, décochez les entreprises à conserver actives,
                  puis confirmez le passage en passif des autres.
                </p>
                <Button
                  type="danger"
                  icon="rollback"
                  onClick={this.openPassiveModal}
                >
                  Passer les entreprises actives en passif
                </Button>
              </Panel>
            ) : null}
          </Collapse>
        </Form>

        <Modal
          title="Confirmation : passer les entreprises actives en passif"
          visible={this.state.passiveModalVisible}
          width={920}
          onCancel={this.closePassiveModal}
          closable={!this.state.passiveSaving}
          maskClosable={false}
          keyboard={!this.state.passiveSaving}
          destroyOnClose
          footer={[
            <Button
              key="cancel"
              onClick={this.closePassiveModal}
              disabled={this.state.passiveSaving}
            >
              Annuler
            </Button>,
            <Button
              key="confirm"
              type="danger"
              onClick={this.setAllCompaniesPassive}
              loading={this.state.passiveSaving}
              disabled={
                this.state.passivePreviewLoading ||
                !this.state.passivePreviewToken ||
                selectedCompanyCount === 0 ||
                !this.state.passiveConfirmed ||
                Boolean(this.state.passiveError)
              }
            >
              Confirmer le passage en passif
            </Button>,
          ]}
        >
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              selectedCompanyCount +
              " entreprise(s) seront passée(s) en passif."
            }
            description="Décochez les entreprises à conserver actives. Votre sélection est conservée lorsque vous changez de page."
          />

          {this.state.passiveError ? (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="Une erreur est survenue"
              description={this.state.passiveError}
            />
          ) : null}

          <div style={{ marginBottom: 12, textAlign: "right" }}>
            <Button
              icon="reload"
              disabled={
                this.state.passiveSaving ||
                this.state.passivePreviewLoading
              }
              onClick={() => this.loadActiveCompanies(1)}
            >
              Actualiser la liste
            </Button>
          </div>

          <Table
            rowKey="_id"
            size="middle"
            className="admin-passive-companies-table"
            loading={this.state.passivePreviewLoading}
            dataSource={this.state.passiveCompanies}
            rowSelection={{
              selectedRowKeys,
              onSelect: (company, selected) => {
                this.setState((state) => {
                  const excludedCompanyIds = new Set(
                    state.passiveExcludedCompanyIds
                  );

                  if (selected) {
                    excludedCompanyIds.delete(String(company._id));
                  } else {
                    excludedCompanyIds.add(String(company._id));
                  }

                  return {
                    passiveExcludedCompanyIds: Array.from(excludedCompanyIds),
                    passiveConfirmed: false,
                  };
                });
              },
              onSelectAll: (selected, selectedRows, changeRows) => {
                this.setState((state) => {
                  const excludedCompanyIds = new Set(
                    state.passiveExcludedCompanyIds
                  );

                  changeRows.forEach((company) => {
                    if (selected) {
                      excludedCompanyIds.delete(String(company._id));
                    } else {
                      excludedCompanyIds.add(String(company._id));
                    }
                  });

                  return {
                    passiveExcludedCompanyIds: Array.from(excludedCompanyIds),
                    passiveConfirmed: false,
                  };
                });
              },
            }}
            columns={[
              {
                title: "Entreprise",
                dataIndex: "name",
                key: "name",
                width: 270,
                render: (value) => {
                  const companyName = value || "Sans nom";

                  return (
                    <span
                      className="admin-passive-company-name"
                      title={companyName}
                    >
                      {companyName}
                    </span>
                  );
                },
              },
              {
                title: "Ville",
                dataIndex: "city",
                key: "city",
                width: 150,
                render: (value) => value || "—",
              },
              {
                title: "Email",
                dataIndex: "email",
                key: "email",
                width: 220,
                render: (value) => value || "—",
              },
              {
                title: "Statut actuel",
                dataIndex: "status",
                key: "status",
                width: 140,
                render: () => <Tag color="green">Actif</Tag>,
              },
            ]}
            pagination={{
              current: this.state.passivePage,
              pageSize: this.state.passivePageSize,
              total: this.state.passiveTotal,
              showSizeChanger: false,
              showTotal: (total) => total + " entreprise(s)",
            }}
            onChange={(pagination) => {
              if (pagination.current !== this.state.passivePage) {
                this.loadActiveCompanies(pagination.current);
              }
            }}
            locale={{
              emptyText: "Aucune entreprise active.",
            }}
            scroll={{ x: 840 }}
          />

          <Checkbox
            checked={this.state.passiveConfirmed}
            disabled={
              this.state.passivePreviewLoading ||
              this.state.passiveSaving ||
              !this.state.passivePreviewToken ||
              selectedCompanyCount === 0 ||
              Boolean(this.state.passiveError)
            }
            onChange={(event) =>
              this.setState({
                passiveConfirmed: event.target.checked,
              })
            }
          >
            J'ai vérifié la liste et je confirme le passage en passif des{" "}
            {selectedCompanyCount} entreprise(s) sélectionnées.
          </Checkbox>
        </Modal>

      </div>
    );
  }
}

export default Form.create()(AdminSettings);



// WEBPACK FOOTER //
// ./components/admin-settings.js